import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import type {
  Node as ReactFlowNode,
  Edge as ReactFlowEdge,
} from "@xyflow/react";
import { JSONValue } from "@/components/jsonSchemaForm/types";
import { DEFAULT_AGENT_TIMEZONE } from "@forgepoint/shared";

// Backend types - these match the backend interfaces exactly
export type AIConfiguration = {
  type: "AI";
  prompt: string;
  schemaPath: string[];
};

export type ManualConfiguration = {
  type: "MANUAL";
  input?: JSONValue | undefined;
  label?: string | undefined;
  schemaPath: string[];
};

export type ActionConfiguration = AIConfiguration | ManualConfiguration;

export type ReasoningLevel = "high" | "medium" | "low";

export interface ModelConfig {
  model: string;
  reasoningLevel?: ReasoningLevel;
}

type UserSelector = {
  type: "USER";
  userId: string;
};

export type ApprovalConfig = {
  approvers: UserSelector[];
};

export interface BaseNode {
  id: string;
  position: { x: number; y: number };
}

interface BaseActionNode extends BaseNode {
  type: "ACTION" | "CONDITION" | "MESSAGE" | "AGENT";
  actionId: string;
  actionConfiguration: ActionConfiguration[];
  approvalConfig?: ApprovalConfig;
  modelConfig?: ModelConfig;
  connectionConfig?: { connectionId: string };
}

export interface ActionNode extends BaseActionNode {
  type: "ACTION" | "CONDITION" | "MESSAGE";
}

export interface AgentNode extends BaseActionNode {
  type: "AGENT";
  actions: ActionNode[];
  prompt?: string;
  shouldSummarize?: boolean;
}

export interface TriggerNode extends BaseNode {
  type: "TRIGGER";
  triggerId: string;
  triggerConfiguration: ManualConfiguration[];
  connectionConfig?: { connectionId: string };
}

export type Node = ActionNode | TriggerNode | AgentNode;

export type FlowEditorActionNode = ReactFlowNode<
  { node: ActionNode; workflowVersionId?: string },
  "ACTION"
>;
export type FlowEditorTriggerNode = ReactFlowNode<
  { node: TriggerNode; workflowVersionId?: string },
  "TRIGGER"
>;
export type FlowEditorConditionNode = ReactFlowNode<
  { node: ActionNode; workflowVersionId?: string },
  "CONDITION"
>;
export type FlowEditorAgentNode = ReactFlowNode<
  { node: AgentNode; workflowVersionId?: string },
  "AGENT"
>;

export type FlowEditorMessageNode = ReactFlowNode<
  { node: ActionNode; workflowVersionId?: string },
  "MESSAGE"
>;

export type FlowEditorNode =
  | FlowEditorActionNode
  | FlowEditorTriggerNode
  | FlowEditorConditionNode
  | FlowEditorMessageNode
  | FlowEditorAgentNode;

export type Edge = {
  id: string;
  condition: string;
  from: string;
  to: string;
};

export type FlowEditorEdge = ReactFlowEdge<{ edge: Edge }, "DEFAULT">;

export type AgentWorkflowDefinition = {
  nodes: Node[];
  edges: Edge[];
};

export interface WorkflowVersionTrigger {
  id: string;
  agentWorkflowVersionId: string;
  triggerId: string;
  triggerNode: TriggerNode;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowVersion {
  id: string;
  agentWorkflowId: string;
  version: number;
  definition: AgentWorkflowDefinition;
  createdByUserId: string;
  status: "DRAFT" | "ACTIVE" | "RETIRED";
  createdAt: string;
  updatedAt: string;
  triggers?: WorkflowVersionTrigger[];
  allowsChatReplies?: boolean;
  allowsChat?: boolean;
  errors?: Record<string, string[]>;
  prompt?: string;
  timezone?: string;
  loadRules?: boolean;
}

export interface Agent {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  createdByUserId: string;
  status: "ACTIVE" | "DISABLED";
  createdAt: string;
  updatedAt: string;
  workflowVersionIds: string[];
  activeVersionId?: string;
}

export interface AgentState {
  isLoadingGet: boolean;
  isLoadingUpdate: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  updateError: string | null;
  deleteError: string | null;
}

export interface WorkflowVersionState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDeploy: boolean;
  getError: string | null;
  saveError: string | null;
  deployError: string | null;
  lastSavedAt: string | null;
}

export interface CreateAgentData {
  name: string;
  description?: string;
}

export interface UpdateAgentData {
  name?: string;
  description?: string;
  status?: "ACTIVE" | "DISABLED";
}

const normalizeWorkflowVersion = (
  version: WorkflowVersion,
): WorkflowVersion => ({
  ...version,
  timezone: version.timezone ?? DEFAULT_AGENT_TIMEZONE,
});

interface AgentWorkflowStore {
  agentMap: Map<string, Agent>;
  agentStateMap: Map<string, AgentState>;
  agentIds: string[];
  isLoadingList: boolean;
  listError: string | null;

  // Workflow version related state
  workflowVersionMap: Map<string, WorkflowVersion>;
  workflowVersionStateMap: Map<string, WorkflowVersionState>;
  workflowDefinitionMap: Map<string, AgentWorkflowDefinition>;

  fetchAgents: () => Promise<void>;
  createAgent: (
    agentData: CreateAgentData,
  ) => Promise<{ agent: Agent | null; error: string | null }>;
  updateAgent: (
    agentId: string,
    agentData: UpdateAgentData,
  ) => Promise<{ agent: Agent | null; error: string | null }>;
  getAgent: (agentId: string) => Promise<void>;
  getAgentById: (agentId: string) => Agent | undefined;
  getAgentState: (agentId: string) => AgentState;

  // Workflow version methods
  getWorkflowVersions: (workflowId: string) => Promise<void>;
  getActiveWorkflowVersion: (workflowId: string) => Promise<void>;
  updateWorkflowDefinition: (
    workflowId: string,
    definition: AgentWorkflowDefinition,
  ) => void;
  updateWorkflowVersionPrompt: (
    workflowId: string,
    data: { prompt?: string; timezone?: string; loadRules?: boolean },
  ) => void;
  saveWorkflowVersion: (workflowId: string) => Promise<WorkflowVersion | null>;
  deployWorkflow: (workflowId: string) => Promise<boolean>;
  getLatestWorkflowVersion: (workflowId: string) => WorkflowVersion | undefined;
  getActiveVersion: (workflowId: string) => WorkflowVersion | undefined;
  getWorkflowDefinition: (
    workflowId: string,
  ) => AgentWorkflowDefinition | undefined;
  getVersionState: (workflowId: string) => WorkflowVersionState;
  getWorkflowVersionById: (
    workflowVersionId: string,
  ) => WorkflowVersion | undefined;
}

const getDefaultAgentState = (): AgentState => ({
  isLoadingGet: false,
  isLoadingUpdate: false,
  isLoadingDelete: false,
  getError: null,
  updateError: null,
  deleteError: null,
});

const getDefaultVersionState = (): WorkflowVersionState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDeploy: false,
  getError: null,
  saveError: null,
  deployError: null,
  lastSavedAt: null,
});

export const useAgentWorkflowStore = create<AgentWorkflowStore>((set, get) => ({
  agentMap: new Map(),
  agentStateMap: new Map(),
  agentIds: [],
  isLoadingList: false,
  listError: null,

  // Workflow version related state
  workflowVersionMap: new Map(),
  workflowVersionStateMap: new Map(),
  workflowDefinitionMap: new Map(),

  fetchAgents: async () => {
    set({ isLoadingList: true, listError: null });

    try {
      const response = await fetchApi("agents/workflows", "GET");

      if (response.ok && response.data) {
        const data = response.data as {
          workflows: Array<Agent & { workflowVersions?: WorkflowVersion[] }>;
        };
        const newAgentMap = new Map<string, Agent>();
        const newAgentStateMap = new Map<string, AgentState>();
        const newWorkflowVersionMap = new Map<string, WorkflowVersion>();
        const newAgentIds: string[] = [];

        data.workflows.forEach((workflow) => {
          // Extract workflow versions if they exist
          const workflowVersions = (workflow.workflowVersions || []).map(
            (version) => normalizeWorkflowVersion(version),
          );
          const workflowVersionIds: string[] = [];

          // Populate the workflowVersionMap
          workflowVersions.forEach((version) => {
            newWorkflowVersionMap.set(version.id, version);
            workflowVersionIds.push(version.id);
          });

          // Create the agent object with workflowVersionIds
          const agent: Agent = {
            id: workflow.id,
            name: workflow.name,
            description: workflow.description,
            organizationId: workflow.organizationId,
            createdByUserId: workflow.createdByUserId,
            status: workflow.status,
            createdAt: workflow.createdAt,
            updatedAt: workflow.updatedAt,
            workflowVersionIds,
            activeVersionId: workflow.activeVersionId,
          };

          newAgentMap.set(agent.id, agent);
          newAgentIds.push(agent.id);

          // Initialize state for each agent if not exists
          if (!get().agentStateMap.has(agent.id)) {
            newAgentStateMap.set(agent.id, getDefaultAgentState());
          }

          // Initialize workflow definition if we have versions
          if (workflowVersions.length > 0) {
            const latestVersion = workflowVersions.sort(
              (a, b) => b.version - a.version,
            )[0];
            set((state) => {
              const newDefinitionMap = new Map(state.workflowDefinitionMap);
              newDefinitionMap.set(workflow.id, latestVersion.definition);
              return { workflowDefinitionMap: newDefinitionMap };
            });
          }
        });

        set((state) => ({
          agentMap: newAgentMap,
          agentStateMap: new Map([...state.agentStateMap, ...newAgentStateMap]),
          workflowVersionMap: new Map([
            ...state.workflowVersionMap,
            ...newWorkflowVersionMap,
          ]),
          agentIds: newAgentIds,
          isLoadingList: false,
        }));
        return;
      }

      set({ listError: "Failed to fetch agents", isLoadingList: false });
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch agents", isLoadingList: false });
    }
  },

  createAgent: async (agentData: CreateAgentData) => {
    try {
      const response = await fetchApi("agents/workflows", "POST", {
        body: {
          name: agentData.name,
        },
      });

      if (response.ok && response.data) {
        const data = response.data as {
          workflow: Agent & { workflowVersions?: WorkflowVersion[] };
        };
        const workflow = data.workflow;
        const workflowVersions = (workflow.workflowVersions || []).map(
          (version) => normalizeWorkflowVersion(version),
        );
        const workflowVersionIds: string[] = [];

        set((state) => {
          const newAgentMap = new Map(state.agentMap);
          const newAgentStateMap = new Map(state.agentStateMap);
          const newWorkflowVersionMap = new Map(state.workflowVersionMap);

          // Populate workflow versions if they exist
          workflowVersions.forEach((version) => {
            newWorkflowVersionMap.set(version.id, version);
            workflowVersionIds.push(version.id);
          });

          // Create the agent object with workflowVersionIds
          const newAgent: Agent = {
            id: workflow.id,
            name: workflow.name,
            description: workflow.description,
            organizationId: workflow.organizationId,
            createdByUserId: workflow.createdByUserId,
            status: workflow.status,
            createdAt: workflow.createdAt,
            updatedAt: workflow.updatedAt,
            workflowVersionIds,
            activeVersionId: workflow.activeVersionId,
          };

          newAgentMap.set(newAgent.id, newAgent);
          newAgentStateMap.set(newAgent.id, getDefaultAgentState());

          return {
            agentMap: newAgentMap,
            agentStateMap: newAgentStateMap,
            workflowVersionMap: newWorkflowVersionMap,
            agentIds: [...state.agentIds, newAgent.id],
          };
        });

        return { agent: workflow, error: null };
      }

      return {
        agent: null,
        error: response.error ?? "Failed to create agent",
      };
    } catch (error) {
      captureException(error as Error);
      return { agent: null, error: "Failed to create agent" };
    }
  },

  updateAgent: async (agentId: string, agentData: UpdateAgentData) => {
    // Update loading state for this agent
    set((state) => {
      const newAgentStateMap = new Map(state.agentStateMap);
      const currentState =
        newAgentStateMap.get(agentId) || getDefaultAgentState();
      newAgentStateMap.set(agentId, {
        ...currentState,
        isLoadingUpdate: true,
        updateError: null,
      });
      return { agentStateMap: newAgentStateMap };
    });

    try {
      const response = await fetchApi(`agents/workflows/${agentId}`, "PATCH", {
        body: agentData,
      });

      if (response.ok && response.data) {
        const data = response.data as { workflow: Agent };
        const updatedAgent = data.workflow;

        set((state) => {
          const newAgentMap = new Map(state.agentMap);
          const currentAgent = newAgentMap.get(agentId);
          newAgentMap.set(agentId, { ...currentAgent, ...updatedAgent });

          const newAgentStateMap = new Map(state.agentStateMap);
          const currentState =
            newAgentStateMap.get(agentId) || getDefaultAgentState();
          newAgentStateMap.set(agentId, {
            ...currentState,
            isLoadingUpdate: false,
            updateError: null,
          });

          return {
            agentMap: newAgentMap,
            agentStateMap: newAgentStateMap,
          };
        });

        return { agent: updatedAgent, error: null };
      }

      const errorMessage = response.error ?? "Failed to update agent";
      set((state) => {
        const newAgentStateMap = new Map(state.agentStateMap);
        const currentState =
          newAgentStateMap.get(agentId) || getDefaultAgentState();
        newAgentStateMap.set(agentId, {
          ...currentState,
          isLoadingUpdate: false,
          updateError: errorMessage,
        });
        return { agentStateMap: newAgentStateMap };
      });
      return { agent: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newAgentStateMap = new Map(state.agentStateMap);
        const currentState =
          newAgentStateMap.get(agentId) || getDefaultAgentState();
        newAgentStateMap.set(agentId, {
          ...currentState,
          isLoadingUpdate: false,
          updateError: "Failed to update agent",
        });
        return { agentStateMap: newAgentStateMap };
      });
      return { agent: null, error: "Failed to update agent" };
    }
  },

  getAgent: async (agentId: string) => {
    // Check if we already have this agent
    const existingAgent = get().agentMap.get(agentId);
    if (existingAgent) {
      return;
    }

    // Update loading state for this agent
    set((state) => {
      const newAgentStateMap = new Map(state.agentStateMap);
      const currentState =
        newAgentStateMap.get(agentId) || getDefaultAgentState();
      newAgentStateMap.set(agentId, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { agentStateMap: newAgentStateMap };
    });

    try {
      const response = await fetchApi(`agents/workflows/${agentId}`, "GET");

      if (response.ok && response.data) {
        const data = response.data as {
          workflow: Agent & { workflowVersions?: WorkflowVersion[] };
        };
        const workflow = data.workflow;
        const workflowVersions = (workflow.workflowVersions || []).map(
          (version) => normalizeWorkflowVersion(version),
        );
        const workflowVersionIds: string[] = [];

        set((state) => {
          const newAgentMap = new Map(state.agentMap);
          const newWorkflowVersionMap = new Map(state.workflowVersionMap);
          const newDefinitionMap = new Map(state.workflowDefinitionMap);

          // Populate workflow versions if they exist
          workflowVersions.forEach((version) => {
            newWorkflowVersionMap.set(version.id, version);
            workflowVersionIds.push(version.id);
          });

          // Initialize workflow definition if we have versions
          if (workflowVersions.length > 0) {
            const latestVersion = workflowVersions.sort(
              (a, b) => b.version - a.version,
            )[0];
            newDefinitionMap.set(agentId, latestVersion.definition);
          }

          // Create the agent object with workflowVersionIds
          const agent: Agent = {
            id: workflow.id,
            name: workflow.name,
            description: workflow.description,
            organizationId: workflow.organizationId,
            createdByUserId: workflow.createdByUserId,
            status: workflow.status,
            createdAt: workflow.createdAt,
            updatedAt: workflow.updatedAt,
            workflowVersionIds,
            activeVersionId: workflow.activeVersionId,
          };

          newAgentMap.set(agentId, agent);

          // Add to agentIds if not already there
          const newAgentIds = state.agentIds.includes(agentId)
            ? state.agentIds
            : [...state.agentIds, agentId];

          const newAgentStateMap = new Map(state.agentStateMap);
          const currentState =
            newAgentStateMap.get(agentId) || getDefaultAgentState();
          newAgentStateMap.set(agentId, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });

          return {
            agentMap: newAgentMap,
            workflowVersionMap: newWorkflowVersionMap,
            workflowDefinitionMap: newDefinitionMap,
            agentIds: newAgentIds,
            agentStateMap: newAgentStateMap,
          };
        });
        return;
      }

      set((state) => {
        const newAgentStateMap = new Map(state.agentStateMap);
        const currentState =
          newAgentStateMap.get(agentId) || getDefaultAgentState();
        newAgentStateMap.set(agentId, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch agent",
        });
        return { agentStateMap: newAgentStateMap };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newAgentStateMap = new Map(state.agentStateMap);
        const currentState =
          newAgentStateMap.get(agentId) || getDefaultAgentState();
        newAgentStateMap.set(agentId, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch agent",
        });
        return { agentStateMap: newAgentStateMap };
      });
    }
  },

  getAgentById: (agentId: string) => {
    return get().agentMap.get(agentId);
  },

  getAgentState: (agentId: string) => {
    return get().agentStateMap.get(agentId) || getDefaultAgentState();
  },

  // Workflow version methods
  getWorkflowVersions: async (workflowId: string) => {
    set((state) => {
      const newStateMap = new Map(state.workflowVersionStateMap);
      const currentState =
        newStateMap.get(workflowId) || getDefaultVersionState();
      newStateMap.set(workflowId, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { workflowVersionStateMap: newStateMap };
    });

    try {
      const response = await fetchApi(`agents/workflows/${workflowId}`, "GET");

      if (response.ok && response.data) {
        const data = response.data as {
          workflow: { workflowVersions: WorkflowVersion[] };
        };
        const versions = (data.workflow.workflowVersions || []).map((version) =>
          normalizeWorkflowVersion(version),
        );

        if (versions && versions.length > 0) {
          const latestVersion = versions.sort(
            (a, b) => b.version - a.version,
          )[0];

          set((state) => {
            const newVersionMap = new Map(state.workflowVersionMap);
            const newDefinitionMap = new Map(state.workflowDefinitionMap);
            const newStateMap = new Map(state.workflowVersionStateMap);
            const newAgentMap = new Map(state.agentMap);

            // Update workflowVersionMap with all versions
            const versionIds: string[] = [];
            versions.forEach((version) => {
              newVersionMap.set(version.id, version);
              versionIds.push(version.id);
            });

            // Update the agent's workflowVersionIds
            const agent = newAgentMap.get(workflowId);
            if (agent) {
              agent.workflowVersionIds = versionIds;
              newAgentMap.set(workflowId, agent);
            }

            // Set the latest version's definition
            newDefinitionMap.set(workflowId, latestVersion.definition);

            const currentState =
              newStateMap.get(workflowId) || getDefaultVersionState();
            newStateMap.set(workflowId, {
              ...currentState,
              isLoadingGet: false,
              getError: null,
            });

            return {
              workflowVersionMap: newVersionMap,
              workflowDefinitionMap: newDefinitionMap,
              workflowVersionStateMap: newStateMap,
              agentMap: newAgentMap,
            };
          });
          return;
        }

        // No versions exist yet, initialize with empty definition
        set((state) => {
          const newDefinitionMap = new Map(state.workflowDefinitionMap);
          const newStateMap = new Map(state.workflowVersionStateMap);

          newDefinitionMap.set(workflowId, { nodes: [], edges: [] });

          const currentState =
            newStateMap.get(workflowId) || getDefaultVersionState();
          newStateMap.set(workflowId, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });

          return {
            workflowDefinitionMap: newDefinitionMap,
            workflowVersionStateMap: newStateMap,
          };
        });
        return;
      }

      set((state) => {
        const newStateMap = new Map(state.workflowVersionStateMap);
        const currentState =
          newStateMap.get(workflowId) || getDefaultVersionState();
        newStateMap.set(workflowId, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to load workflow versions",
        });
        return { workflowVersionStateMap: newStateMap };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newStateMap = new Map(state.workflowVersionStateMap);
        const currentState =
          newStateMap.get(workflowId) || getDefaultVersionState();
        newStateMap.set(workflowId, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to load workflow versions",
        });
        return { workflowVersionStateMap: newStateMap };
      });
    }
  },

  getActiveWorkflowVersion: async (workflowId: string) => {
    set((state) => {
      const newStateMap = new Map(state.workflowVersionStateMap);
      const currentState =
        newStateMap.get(workflowId) || getDefaultVersionState();
      newStateMap.set(workflowId, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { workflowVersionStateMap: newStateMap };
    });

    try {
      const response = await fetchApi(
        `agents/workflows/${workflowId}/active-version`,
        "GET",
      );

      if (response.ok && response.data) {
        const data = response.data as {
          workflowVersion: WorkflowVersion | null;
        };
        const activeVersion = data.workflowVersion
          ? normalizeWorkflowVersion(data.workflowVersion)
          : null;

        set((state) => {
          const newVersionMap = new Map(state.workflowVersionMap);
          const newStateMap = new Map(state.workflowVersionStateMap);
          const newAgentMap = new Map(state.agentMap);

          if (activeVersion) {
            // Add the active version to the map
            newVersionMap.set(activeVersion.id, activeVersion);

            // Update the agent's activeVersionId
            const agent = newAgentMap.get(workflowId);
            if (agent) {
              agent.activeVersionId = activeVersion.id;
              newAgentMap.set(workflowId, agent);
            }
          }

          const currentState =
            newStateMap.get(workflowId) || getDefaultVersionState();
          newStateMap.set(workflowId, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });

          return {
            workflowVersionMap: newVersionMap,
            workflowVersionStateMap: newStateMap,
            agentMap: newAgentMap,
          };
        });
        return;
      }

      set((state) => {
        const newStateMap = new Map(state.workflowVersionStateMap);
        const currentState =
          newStateMap.get(workflowId) || getDefaultVersionState();
        newStateMap.set(workflowId, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to load active workflow version",
        });
        return { workflowVersionStateMap: newStateMap };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newStateMap = new Map(state.workflowVersionStateMap);
        const currentState =
          newStateMap.get(workflowId) || getDefaultVersionState();
        newStateMap.set(workflowId, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to load active workflow version",
        });
        return { workflowVersionStateMap: newStateMap };
      });
    }
  },

  updateWorkflowDefinition: (
    workflowId: string,
    definition: AgentWorkflowDefinition,
  ) => {
    set((state) => {
      const newDefinitionMap = new Map(state.workflowDefinitionMap);
      newDefinitionMap.set(workflowId, definition);
      return { workflowDefinitionMap: newDefinitionMap };
    });
  },

  updateWorkflowVersionPrompt: (
    workflowId: string,
    data: { prompt?: string; timezone?: string; loadRules?: boolean },
  ) => {
    set((state) => {
      const newVersionMap = new Map(state.workflowVersionMap);
      const latestVersion = get().getLatestWorkflowVersion(workflowId);
      if (latestVersion) {
        newVersionMap.set(latestVersion.id, {
          ...latestVersion,
          prompt:
            data.prompt !== undefined ? data.prompt : latestVersion.prompt,
          timezone:
            data.timezone ?? latestVersion.timezone ?? DEFAULT_AGENT_TIMEZONE,
          loadRules:
            data.loadRules !== undefined
              ? data.loadRules
              : latestVersion.loadRules,
        });
      }
      return { workflowVersionMap: newVersionMap };
    });
  },

  saveWorkflowVersion: async (workflowId: string) => {
    const definition = get().workflowDefinitionMap.get(workflowId);
    if (!definition) {
      return null;
    }

    const latestVersion = get().getLatestWorkflowVersion(workflowId);
    const prompt = latestVersion?.prompt;
    const timezone = latestVersion?.timezone ?? DEFAULT_AGENT_TIMEZONE;
    const loadRules = latestVersion?.loadRules ?? false;

    set((state) => {
      const newStateMap = new Map(state.workflowVersionStateMap);
      const currentState =
        newStateMap.get(workflowId) || getDefaultVersionState();
      newStateMap.set(workflowId, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { workflowVersionStateMap: newStateMap };
    });

    try {
      const response = await fetchApi(
        `agents/workflows/${workflowId}/version`,
        "PUT",
        {
          body: { definition, prompt, timezone, loadRules },
        },
      );

      if (response.ok && response.data) {
        const data = response.data as { workflowVersion: WorkflowVersion };
        const newVersion = normalizeWorkflowVersion(data.workflowVersion);

        set((state) => {
          const newVersionMap = new Map(state.workflowVersionMap);
          const newStateMap = new Map(state.workflowVersionStateMap);
          const newAgentMap = new Map(state.agentMap);

          // Add the new version to the map
          newVersionMap.set(newVersion.id, newVersion);

          // Update the agent's workflowVersionIds
          const agent = newAgentMap.get(workflowId);
          if (agent && !agent.workflowVersionIds.includes(newVersion.id)) {
            agent.workflowVersionIds.push(newVersion.id);
            newAgentMap.set(workflowId, agent);
          }

          const currentState =
            newStateMap.get(workflowId) || getDefaultVersionState();
          newStateMap.set(workflowId, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
            lastSavedAt: new Date().toISOString(),
          });

          return {
            workflowVersionMap: newVersionMap,
            workflowVersionStateMap: newStateMap,
            agentMap: newAgentMap,
          };
        });

        return newVersion;
      }

      set((state) => {
        const newStateMap = new Map(state.workflowVersionStateMap);
        const currentState =
          newStateMap.get(workflowId) || getDefaultVersionState();
        newStateMap.set(workflowId, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to save workflow version",
        });
        return { workflowVersionStateMap: newStateMap };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newStateMap = new Map(state.workflowVersionStateMap);
        const currentState =
          newStateMap.get(workflowId) || getDefaultVersionState();
        newStateMap.set(workflowId, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to save workflow version",
        });
        return { workflowVersionStateMap: newStateMap };
      });
      return null;
    }
  },

  deployWorkflow: async (workflowId: string) => {
    set((state) => {
      const newStateMap = new Map(state.workflowVersionStateMap);
      const currentState =
        newStateMap.get(workflowId) || getDefaultVersionState();
      newStateMap.set(workflowId, {
        ...currentState,
        isLoadingDeploy: true,
        deployError: null,
      });
      return { workflowVersionStateMap: newStateMap };
    });

    try {
      const response = await fetchApi(
        `agents/workflows/${workflowId}/deploy`,
        "POST",
      );

      if (response.ok && response.data) {
        const data = response.data as {
          workflow: {
            id: string;
            status: "ACTIVE" | "DISABLED";
            activeVersionId: string;
          };
          workflowVersion: WorkflowVersion;
        };
        const deployedVersion = normalizeWorkflowVersion(data.workflowVersion);

        set((state) => {
          const newVersionMap = new Map(state.workflowVersionMap);
          const newStateMap = new Map(state.workflowVersionStateMap);
          const newAgentMap = new Map(state.agentMap);

          // Add the deployed version to the map
          newVersionMap.set(deployedVersion.id, deployedVersion);

          // Update the agent's activeVersionId
          const agent = newAgentMap.get(workflowId);
          if (agent) {
            agent.activeVersionId = deployedVersion.id;
            if (!agent.workflowVersionIds.includes(deployedVersion.id)) {
              agent.workflowVersionIds.push(deployedVersion.id);
            }
            newAgentMap.set(workflowId, { ...agent, ...data.workflow });
          }

          const currentState =
            newStateMap.get(workflowId) || getDefaultVersionState();
          newStateMap.set(workflowId, {
            ...currentState,
            isLoadingDeploy: false,
            deployError: null,
          });

          return {
            workflowVersionMap: newVersionMap,
            workflowVersionStateMap: newStateMap,
            agentMap: newAgentMap,
          };
        });

        return true;
      }

      set((state) => {
        const newStateMap = new Map(state.workflowVersionStateMap);
        const currentState =
          newStateMap.get(workflowId) || getDefaultVersionState();
        newStateMap.set(workflowId, {
          ...currentState,
          isLoadingDeploy: false,
          deployError: "Failed to deploy workflow",
        });
        return { workflowVersionStateMap: newStateMap };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newStateMap = new Map(state.workflowVersionStateMap);
        const currentState =
          newStateMap.get(workflowId) || getDefaultVersionState();
        newStateMap.set(workflowId, {
          ...currentState,
          isLoadingDeploy: false,
          deployError: "Failed to deploy workflow",
        });
        return { workflowVersionStateMap: newStateMap };
      });
      return false;
    }
  },

  getLatestWorkflowVersion: (workflowId: string) => {
    const agent = get().agentMap.get(workflowId);
    if (!agent || !agent.workflowVersionIds.length) {
      return undefined;
    }

    // Get all versions for this workflow
    const versions = agent.workflowVersionIds
      .map((id) => get().workflowVersionMap.get(id))
      .filter((v): v is WorkflowVersion => v !== undefined);

    // Sort by version number and return the latest
    if (versions.length > 0) {
      return versions.sort((a, b) => b.version - a.version)[0];
    }

    return undefined;
  },

  getActiveVersion: (workflowId: string) => {
    const agent = get().agentMap.get(workflowId);
    if (!agent || !agent.activeVersionId) {
      return undefined;
    }

    return get().workflowVersionMap.get(agent.activeVersionId);
  },

  getWorkflowDefinition: (workflowId: string) => {
    return get().workflowDefinitionMap.get(workflowId);
  },

  getVersionState: (workflowId: string) => {
    return (
      get().workflowVersionStateMap.get(workflowId) || getDefaultVersionState()
    );
  },

  getWorkflowVersionById: (workflowVersionId: string) => {
    return get().workflowVersionMap.get(workflowVersionId);
  },
}));
